import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  StorageScope,
  TObjectShareResponse,
} from "../../../../../services/api/instances/storageApi/types/service_types";
import { useAppDispatch } from "../../../../../services/redux/hooks/use-dispatch";
import {
  createSharedObject,
  deleteSharedObject,
  fetchAllStorageObjectsSharing,
  updateSharedObject,
} from "../../../../../app/state/storageObjectSlice/actions/actions";
import { generateNewPermissionBlock } from "../../../helpers/generateNewPermissionBlock";
import { TPermissionAction } from "../types";
import { useAppSelector } from "../../../../../services/redux/hooks/use-selector";
import { selectSharedObjects } from "../../../../../app/state/storageObjectSlice/selectors";
import { syncPermissionPairs } from "../helpers";
import { flattenPermissionsForUpdate } from "../../../../../app/state/storageObjectSlice/utils/flattenPermissionsForUpdate";
import { flattenPermissions } from "../../../../../app/state/storageObjectSlice/utils/flattenPermissions";

export const useShareObjectModal = (
  service: StorageScope,
  id: string,
  sharedObjects: TObjectShareResponse[],
) => {
  const dispatch = useAppDispatch();
  const accessObjects = useAppSelector(selectSharedObjects);
  const [permissions, setPermissions] =
    useState<TObjectShareResponse[]>(sharedObjects);
  const { t } = useTranslation();

  useEffect(() => {
    setPermissions(sharedObjects);
  }, [id, sharedObjects]);

  const handleAddNewPermissionBlock = () => {
    const newPermissions = [...permissions, ...generateNewPermissionBlock(id)];
    setPermissions(newPermissions);
  };

  const handleChangeFields = useCallback(
    (
      event: string | string[] | Date | null,
      type: TPermissionAction,
      id: string,
    ) => {
      setPermissions((prev) =>
        prev.map((permission) => {
          if (String(permission.id) !== id) return permission;
          switch (type) {
            case "username": {
              if (Array.isArray(event)) {
                return {
                  ...permission,
                  username: event,
                  permission_pairs: syncPermissionPairs(
                    permission.permission_pairs ?? [],
                    event,
                  ),
                };
              }
              break;
            }

            case "validTill":
              if (event instanceof Date && !isNaN(event.getTime())) {
                return {
                  ...permission,
                  valid_till: event.toISOString(),
                  permission_pairs: syncPermissionPairs(
                    permission.permission_pairs ?? [],
                    permission?.permission_pairs?.map((p) => p.username) ?? [],
                  ),
                };
              }
              break;

            case "write":
              if (typeof event === "string") {
                return {
                  ...permission,
                  write: event === t("sidebar.permission_read") ? false : true,
                  permission_pairs: syncPermissionPairs(
                    permission.permission_pairs ?? [],
                    permission?.permission_pairs?.map((p) => p.username) ?? [],
                  ),
                };
              }
              break;
          }

          return permission;
        }),
      );
    },
    [sharedObjects, permissions],
  );

  const handleDeletePermission = useCallback(
    (id: number) => {
      setPermissions((prev) =>
        prev.map((permission) => {
          const pair = permission.permission_pairs?.find((p) => p.id === id);
          if (pair && !pair?.isTemporary) {
            dispatch(deleteSharedObject({ ids: [id], service }));
          }
          const pairs = permission.permission_pairs?.filter((p) => p.id !== id);
          const newNames = Array.isArray(permission.username)
            ? permission.username.filter((u) => u !== pair?.username)
            : [];

          return {
            ...permission,
            username: newNames,
            permission_pairs: pairs,
          };
        }),
      );
    },
    [permissions],
  );

  const handleDeleteAccessBlock = useCallback(
    (id: number) => {
      if (permissions[0].id !== id) {
        setPermissions((prev) => prev?.filter((p) => p.id !== id));
      }
      const target = permissions?.find((permission) => permission.id === id);
      const ids =
        target?.permission_pairs &&
        target?.permission_pairs
          .map((pair) => (!pair.isTemporary ? pair.id : null))
          .filter((p) => p !== null);
      if (target) {
        setPermissions((prev) =>
          prev.map((p) => (p.id === id ? { ...target, username: [] } : p)),
        );
      }
      if (!accessObjects.some((o) => o.id === id) && ids && ids.length !== 0) {
        dispatch(deleteSharedObject({ ids, service }));
      } else if (accessObjects.some((o) => o.id === id)) {
        dispatch(deleteSharedObject({ ids: [id], service }));
      }
    },
    [permissions, accessObjects],
  );

  const handleSubmit = useCallback(() => {
    const updatedObjects = flattenPermissionsForUpdate(permissions);
    const createdObjects = flattenPermissions(permissions);
    dispatch(updateSharedObject(updatedObjects));
    dispatch(
      createSharedObject({
        rawPermissions: createdObjects,
        service,
      }),
    );
  }, [permissions, sharedObjects]);

  return {
    permissions,
    handleChangeFields,
    handleAddNewPermissionBlock,
    handleDeleteAccessBlock,
    handleDeletePermission,
    handleSubmit,
  };
};
